import "./qrCodeEdit.css";
import { Breadcrumb } from "react-bootstrap";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Placeholder from "react-bootstrap/Placeholder";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { UserAuth } from "../../context/AuthContext";

const QrCodeEdit = () => {
  const { user } = UserAuth();
  const location = useLocation();
  const id = location.pathname.split("/")[4];
  const [loading, setLoading] = useState<Boolean>();
  const [formError, setFormError] = useState<boolean>(false);
  const [formErrorMessage, setFormErrorMessage] = useState<string>("");
  const [qrData, setQrData] = useState({
    userId: "",
    title: "",
    description: "",
    image: "",
    fullUrl: "",
    shortenedUrl: "",
    premium: true,
    group: "",
    expiry: "",
    password: "",
    totalVisits: 0,
  });

  useEffect(() => {
    fetchData(id);
  }, []);

  async function fetchData(id: string) {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/qrcodes/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        setLoading(false);
        throw new Error(
          `HTTP Error: ${response.status} - ${response.statusText}`
        );
      }
      const responseData = await response.json();
      const data = responseData.data; // Access the data property from the response

      setQrData({
        userId: data.userId || "",
        title: data.title || "",
        description: data.description || "",
        image: data.image || "",
        fullUrl: data.fullUrl || "",
        shortenedUrl: data.shortenedUrl || "",
        premium: data.premium || true,
        group: data.group || "",
        expiry: data.expiry ? data.expiry.substring(0, 10) : "",
        password: data.password || "",
        totalVisits: data.totalVisits || 0,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching QR code:", error);
      setLoading(false);
    }
  }

  const sanitizeInput = (input: string): string => {
    return input
      .replace(/[<>]/g, "")
      .replace(/javascript:/gi, "")
      .replace(/on\w+=/gi, "");
  };

  const isValidInput = (input: string): boolean => {
    const dangerousPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      /javascript:/gi,
      /data:/gi,
      /vbscript:/gi,
      /on\w+=/gi,
    ];
    return !dangerousPatterns.some((pattern) => pattern.test(input));
  };

  const handleInputChange = (field: keyof typeof qrData, value: string) => {
    const sanitizedValue = sanitizeInput(value);

    if (!isValidInput(value)) {
      setFormError(true);
      setFormErrorMessage(
        "Invalid input detected. Please remove special characters or scripts."
      );
      return;
    }

    const maxLengths: Record<string, number> = {
      title: 25,
      description: 200,
      password: 50,
    };

    if (maxLengths[field] && value.length > maxLengths[field]) {
      setFormError(true);
      setFormErrorMessage(
        `${field} must be less than ${maxLengths[field]} characters`
      );
      return;
    }

    setQrData({
      ...qrData,
      [field]: sanitizedValue,
    });
  };

  async function handleSubmit(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setFormError(false);
    setFormErrorMessage("");

    const inputsToValidate = [
      qrData.title,
      qrData.fullUrl,
      qrData.description,
      qrData.group,
      qrData.password,
    ];

    if (inputsToValidate.some((input) => !isValidInput(input))) {
      setFormError(true);
      setFormErrorMessage(
        "Invalid input detected. Please remove any special characters or scripts."
      );
      return;
    }

    if (!qrData.fullUrl) {
      setFormError(true);
      setFormErrorMessage("Please enter a URL");
      return;
    }

    if (!qrData.title) {
      setFormError(true);
      setFormErrorMessage("Please enter a name/title");
      return;
    }

    function isValidUrl(string: string) {
      try {
        new URL(string);
        return true;
      } catch (err) {
        return false;
      }
    }

    if (!isValidUrl(qrData.fullUrl)) {
      setFormError(true);
      setFormErrorMessage(
        "Please enter a valid URL (e.g., https://example.com)"
      );
      return;
    }

    function isValidName(string: string) {
      if (string.length < 3) {
        return "Name must be at least 3 characters long";
      }
      if (string.length > 25) {
        return "Name must be less than 25 characters long";
      }
      return null;
    }

    const nameError = isValidName(qrData.title);
    if (nameError) {
      setFormError(true);
      setFormErrorMessage(nameError);
      return;
    }

    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/qrcodes/${id}`,
        {
          method: "PATCH",
          body: JSON.stringify(qrData),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `HTTP Error: ${response.status} - ${
            errorData.error || response.statusText
          }`
        );
      }

      const responseData = await response.json();
      console.log("Successfully updated");
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }

  return (
    <div className="qrCodeEdit" role="main">
      {loading && <LoadingSpinner />}
      <h1 className="overview__container__header">QR Creation</h1>
      <Breadcrumb
        className="overview__container__breadcrumbs"
        aria-label="Navigation breadcrumbs"
      >
        <Breadcrumb.Item href="/home">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item href="/dashboard/qrcodes">QR Codes</Breadcrumb.Item>
        <Breadcrumb.Item active>Edit QR Code</Breadcrumb.Item>
      </Breadcrumb>
      <div className="qrCodeEdit__container">
        <form
          action=""
          className="qrCodeCreation__container__form"
          role="form"
          aria-label="QR Code Edit Form"
        >
          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-title">
              Name <span>*</span>
            </label>
            {loading ? (
              <Placeholder xs={8} bg="secondary" size="lg" />
            ) : (
              <input
                id="qr-title"
                type="text"
                value={qrData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                aria-label="QR Code Name"
                aria-required="true"
              />
            )}
          </div>

          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-link">
              Link <span>*</span>
            </label>
            {loading ? (
              <Placeholder xs={8} bg="secondary" size="lg" />
            ) : (
              <input
                id="qr-link"
                type="text"
                value={qrData.fullUrl}
                onChange={(e) => handleInputChange("fullUrl", e.target.value)}
                aria-label="QR Code Link"
                aria-required="true"
              />
            )}
          </div>

          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-description">Description</label>
            {loading ? (
              <Placeholder xs={8} bg="secondary" size="lg" />
            ) : (
              <input
                id="qr-description"
                type="text"
                value={qrData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                aria-label="QR Code Description"
              />
            )}
          </div>

          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-group">Group</label>
            {loading ? (
              <Placeholder xs={8} bg="secondary" size="lg" />
            ) : (
              <input
                id="qr-group"
                type="text"
                value={qrData.group}
                onChange={(e) => handleInputChange("group", e.target.value)}
                aria-label="QR Code Group"
              />
            )}
          </div>

          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-expiry">Expiry</label>
            {loading ? (
              <Placeholder xs={8} bg="secondary" size="lg" />
            ) : (
              <input
                id="qr-expiry"
                type="date"
                value={qrData.expiry}
                onChange={(e) => handleInputChange("expiry", e.target.value)}
                aria-label="QR Code Expiry Date"
              />
            )}
          </div>

          <div className="qrCodeCreation__container__form__field">
            <label htmlFor="qr-password">Password</label>
            {loading ? (
              <Placeholder xs={8} bg="secondary" size="lg" />
            ) : (
              <input
                id="qr-password"
                type="text"
                placeholder="Enter a password"
                value={qrData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                aria-label="QR Code Password"
              />
            )}
          </div>

          {formError && (
            <b className="form__error" role="alert">
              {formErrorMessage}
            </b>
          )}

          {loading ? (
            <Placeholder xs={8} bg="secondary" size="lg" />
          ) : (
            <button
              className="qrCodeCreation__container__form__submit"
              onClick={(e) => handleSubmit(e)}
              type="submit"
              aria-label="Update QR Code"
            >
              Update QR Code
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default QrCodeEdit;
